import { Clear as ClearIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { adminOrderItemService } from "../../../services/adminOrderItemService";
import { ItemStatus } from "../../../types/admin";
import { getItemStatusColor } from "../../../utils/statusColors";

import BulkEditItemFieldsModal from "./BulkEditItemFieldsModal";

interface CrossOrderBulkBarProps {
  selectedItems: Map<string, Set<string>>; // orderId -> Set<itemId>
  itemStatuses: ItemStatus[];
  onClearSelection: () => void;
  onBulkActionComplete: () => void;
}

const CrossOrderBulkBar: React.FC<CrossOrderBulkBarProps> = ({
  selectedItems,
  itemStatuses,
  onClearSelection,
  onBulkActionComplete,
}) => {
  const { t } = useTranslation();
  const [bulkStatusId, setBulkStatusId] = useState<number | "">("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  // Flatten all selected item IDs
  const allItemIds: string[] = [];
  let orderCount = 0;
  selectedItems.forEach((itemIds) => {
    if (itemIds.size > 0) {
      orderCount++;
      itemIds.forEach((id) => allItemIds.push(id));
    }
  });

  if (allItemIds.length === 0) return null;

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatusId || allItemIds.length === 0) return;
    try {
      setBulkLoading(true);
      await adminOrderItemService.bulkUpdateItemStatus({
        orderItemIds: allItemIds,
        newItemStatusId: Number(bulkStatusId),
        sendNotification: true,
      });
      setSnackbar({
        open: true,
        message: t("admin.orders.bulkStatusUpdateSuccess", { count: allItemIds.length }),
        severity: "success",
      });
      setBulkStatusId("");
      onClearSelection();
      onBulkActionComplete();
    } catch {
      setSnackbar({
        open: true,
        message: t("admin.orders.bulkStatusUpdateFailed"),
        severity: "error",
      });
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <>
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          backgroundColor: "#e3f2fd",
          borderTop: "2px solid #1976d2",
        }}
      >
        <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
          {t("admin.orders.itemsSelectedAcrossOrders", {
            items: allItemIds.length,
            orders: orderCount,
          })}
        </Typography>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t("admin.orders.updateItemStatus")}</InputLabel>
          <Select
            value={bulkStatusId}
            onChange={(e) => setBulkStatusId(e.target.value as number)}
            label={t("admin.orders.updateItemStatus")}
            disabled={bulkLoading}
          >
            {itemStatuses.map((status) => (
              <MenuItem key={status.id} value={status.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: getItemStatusColor(status),
                    }}
                  />
                  <Typography variant="body2">{status.displayName}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          size="small"
          onClick={handleBulkStatusUpdate}
          disabled={bulkLoading || !bulkStatusId}
        >
          {bulkLoading ? t("admin.orders.updating") : t("admin.orders.bulkUpdateStatus")}
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={() => setBulkEditModalOpen(true)}
          disabled={bulkLoading}
        >
          {t("admin.orders.bulkEdit")}
        </Button>

        <Box sx={{ flex: 1 }} />

        <IconButton onClick={onClearSelection} size="small">
          <ClearIcon />
        </IconButton>
      </Paper>

      <BulkEditItemFieldsModal
        open={bulkEditModalOpen}
        onClose={() => setBulkEditModalOpen(false)}
        selectedItemIds={allItemIds}
        onSuccess={() => {
          setSnackbar({
            open: true,
            message: t("admin.orders.bulkEditSuccess", { count: allItemIds.length }),
            severity: "success",
          });
          onClearSelection();
          onBulkActionComplete();
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CrossOrderBulkBar;
